import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnInit, OnDestroy, Output, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { LimitationScreenService } from './limitation-screen.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-limitation-screen',
    templateUrl: './limitation-screen.component.html',
    styleUrls: ['./limitation-screen.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class LimitationScreenComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subs = new Subscription();
  private isInitialized = false;
  private isLoading = false;

  baseURLs: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};
  currentScreenKey = '';

  activeView = '1231';
  groupType = '';
  isCalc = '';
  wsHeader: any = {};
  drdRecord: any = {};
  overrideAmtRecord: any = {};
  drdWorksheetRows: any[] = [];

  // 1231 Gain/Loss override fields (mirrors legacy ctrl.ovverrideamount1231gain*, gain1231comment*)
  override1231Line8: any = '';
  override1231Reclass: any = '';
  override1231Line8Comment = '';
  override1231ReclassComment = '';

  loadingData = true;
  loading = false;
  saving = false;
  errorMessage = '';
  summaryRows: any[] = [];
  detailRows: any[] = [];

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private service: LimitationScreenService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      if (!this.gtwWorkspace.dataFetched.value) {
        this.loadingData = true;
        this.cdr.detectChanges();
        return;
      }
      if (this.isInitialized) {
        this.loadActiveTab();
      } else {
        this.tryInit();
      }
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      if (!this.gtwWorkspace.dataFetched.value) {
        this.loadingData = true;
        this.cdr.detectChanges();
        return;
      }
      if (this.isInitialized) {
        this.loadActiveTab();
      } else {
        this.tryInit();
      }
    }));
    this.tryInit();
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header.globalParams || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.currentScreenKey = String(activeScreen?.screen_key || activeScreen?.id || '');
    this.activeView = this.viewFromScreenKey(this.currentScreenKey);
    this.cdr.detectChanges();
  }

  private viewFromScreenKey(screenKey: string): string {
    if (screenKey === '11203') return 'summary';
    if (screenKey === '10956') return '1231';
    if (screenKey === '11117') return 'capital';
    if (screenKey === '10955') return 'charitable';
    if (screenKey === '11159') return 'scheduleC';
    return this.activeView || 'summary';
  }

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) return;
    if (!this.gtwWorkspace.dataFetched.value) return;
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api;
    if (!baseUrl) return;
    if (!this.filterParams || Object.keys(this.filterParams).length === 0) return;
    this.isInitialized = true;
    this.loadActiveTab();
  }

  setView(view: string): void {
    this.activeView = view;
    this.loadActiveTab();
  }

  private getBaseParams(actionCode: string): any {
    return {
      action_code: actionCode,
      tax_year: this.filterParams?.tax_year || this.globalService?.tax_year || this.rowData?.TAX_YEAR,
      scenario: this.filterParams?.scenario || this.globalService?.scenario || this.rowData?.SCENARIO,
      jcd_key: this.rowData?.JCD_KEY || this.filterParams?.jcd_key || this.globalService?.jcd_key || 250,
      filing_key: this.filterParams?.filing_group || this.filterParams?.filing_key || '',
      filing_group: this.filterParams?.filing_group || this.filterParams?.filing_key || '',
      ho_leid: this.filterParams?.ho_leid || '',
      ho_leid_limitation: this.filterParams?.ho_leid_limitation || '',
      entity_type: this.filterParams?.entity_type || '',
      sso_id: this.globalService?.sso_id || '',
    };
  }

  private loadActiveTab(): void {
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api;
    if (!baseUrl) return;
    this.loadingData = false;
    this.loading = true;
    this.isLoading = true;
    this.errorMessage = '';
    this.summaryRows = [];
    this.detailRows = [];
    this.groupType = '';
    this.override1231Line8 = '';
    this.override1231Reclass = '';
    this.override1231Line8Comment = '';
    this.override1231ReclassComment = '';
    this.cdr.detectChanges();

    if (this.activeView === '1231') {
      this.loadPair(baseUrl, 'bcw60f', '0r3z9b');
      return;
    }
    if (this.activeView === 'charitable') {
      this.loadPair(baseUrl, 'em9o45', 'inj55i');
      return;
    }
    if (this.activeView === 'scheduleC') {
      this.loadPair(baseUrl, 'yqpcnq', 'fd9a4s');
      return;
    }
    if (this.activeView === 'capital') {
      this.loadSingle(baseUrl, '3mcaxq');
      return;
    }
    this.loadSingle(baseUrl, 'qg4nkb');
  }

  private loadPair(baseUrl: string, summaryCode: string, detailCode: string): void {
    this.service.loadJsonObject(baseUrl, this.getBaseParams(summaryCode)).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (summaryRes: any) => {
        this.summaryRows = Array.isArray(summaryRes?.jsonObject) ? summaryRes.jsonObject : [];
        this.service.loadJsonObject(baseUrl, this.getBaseParams(detailCode)).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: (detailRes: any) => {
            this.detailRows = Array.isArray(detailRes?.jsonObject) ? detailRes.jsonObject : [];
            if (this.activeView === 'scheduleC') {
              this.processScheduleCData(this.detailRows);
            } else if (this.activeView === '1231') {
              this.extract1231Overrides();
            }
            this.setGroupType();
            this.loading = false;
            this.isLoading = false;
            this.cdr.detectChanges();
          },
          error: () => this.setLoadError(),
        });
      },
      error: () => this.setLoadError(),
    });
  }

  private loadSingle(baseUrl: string, code: string): void {
    this.service.loadJsonObject(baseUrl, this.getBaseParams(code)).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        this.detailRows = Array.isArray(res?.jsonObject) ? res.jsonObject : [];
        this.setGroupType();
        this.loading = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: () => this.setLoadError(),
    });
  }

  private setGroupType(): void {
    // Legacy: 1231/charitable read last detail row; capital reads first detail row; limitation summary reads last.
    if (this.activeView === 'capital') {
      this.groupType = this.detailRows.length > 0 ? (this.detailRows[0]?.GROUP_TYPE || '') : '';
      return;
    }
    const fromDetail = this.detailRows.length > 0 ? this.detailRows[this.detailRows.length - 1]?.GROUP_TYPE : '';
    const fromSummary = this.summaryRows.length > 0 ? this.summaryRows[this.summaryRows.length - 1]?.GROUP_TYPE : '';
    this.groupType = fromDetail || fromSummary || '';
  }

  private extract1231Overrides(): void {
    if (this.detailRows.length === 0) return;
    const last = this.detailRows[this.detailRows.length - 1] || {};
    this.override1231Line8 = last.LINE8_OVERRIDE_AMT ?? '';
    this.override1231Reclass = last.RECLASS_OVERRIDE_AMT ?? '';
    this.override1231Line8Comment = last.LINE8_OVERRIDE_COMMENTS ?? '';
    this.override1231ReclassComment = last.RECLASS_OVERRIDE_COMMENTS ?? '';
  }

  private setLoadError(): void {
    this.loading = false;
    this.isLoading = false;
    this.errorMessage = 'Unable to load limitation data for the selected tab.';
    this.cdr.detectChanges();
  }

  onAmountChange(row: any, value: any): void {
    row.AMOUNT = value;
    row.IS_CHANGED_FLAG = 'Y';
  }

  onCapitalAmount2Change(row: any, value: any): void {
    row.AMOUNT_2 = value;
    row.IS_CHANGED_FLAG = 'Y';
  }

  onCapitalCommentsChange(row: any, value: any): void {
    row.COMMENTS = value;
    row.IS_CHANGED_FLAG = 'Y';
  }

  onCharitableAmountChange(row: any, index: number, value: any): void {
    row.AMOUNT = value;
    row.IS_CHANGED_FLAG = 'Y';
    this.recomputeCharitableTotals(index);
  }

  onCharitableCommentsChange(row: any, value: any): void {
    row.COMMENTS = value;
    row.IS_CHANGED_FLAG = 'Y';
  }

  private recomputeCharitableTotals(_index: number): void {
    // Mirror legacy ctrl.setSourceTotal: recompute derived rows when an editable cell changes.
    const rows = this.detailRows;
    if (!rows || rows.length < 15) return;
    const safe = (idx: number): number => Number(rows[idx]?.AMOUNT) || 0;
    // Form 8916 Line 2d1/2d2 row (index 8) = (Charitable Contribution Deduction * Percentage) / 100
    rows[8].AMOUNT = (safe(6) * safe(7)) / 100;
    const diff = safe(1) - safe(8);
    rows[9].AMOUNT = diff < 0 ? 0 : diff;
    rows[12].AMOUNT = safe(1) - safe(8);
    rows[14].AMOUNT = safe(12) + safe(13);
  }

  onScheduleCAmountChange(row: any, value: any): void {
    row.AMOUNT = value;
    row.IS_CHANGED_FLAG = 'Y';
    if (this.overrideAmtRecord) {
      this.overrideAmtRecord.IS_CHANGED_FLAG = 'N';
      this.overrideAmtRecord.AMOUNT = 0;
      this.overrideAmtRecord.COMMENTS = null;
    }
  }

  onScheduleCOverrideChange(): void {
    if (this.overrideAmtRecord) {
      this.overrideAmtRecord.IS_CHANGED_FLAG = 'Y';
    }
  }

  onScheduleCCalcModeChange(value: string): void {
    this.isCalc = value;
    this.cdr.detectChanges();
  }

  on1231OverrideChange(field: 'line8' | 'reclass' | 'line8Comment' | 'reclassComment', value: any): void {
    if (field === 'line8') this.override1231Line8 = value;
    else if (field === 'reclass') this.override1231Reclass = value;
    else if (field === 'line8Comment') this.override1231Line8Comment = value;
    else this.override1231ReclassComment = value;
  }

  onSave(): void {
    if (this.saving) return;
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api;
    if (!baseUrl) return;
    this.saving = true;

    const base = this.getBaseParams('x');
    const settingsRow: any = {
      tax_year: base.tax_year,
      scenario: base.scenario,
      jcd_key: base.jcd_key,
    };
    // 1231 settings payload mirrors legacy save1231Gain: include override amounts + comments.
    if (this.activeView === '1231') {
      settingsRow.reclass_override_amt = this.override1231Reclass;
      settingsRow.reclass_override_comments = this.override1231ReclassComment;
      settingsRow.line8_override_amt = this.override1231Line8;
      settingsRow.line8_override_comments = this.override1231Line8Comment;
    }
    const settings = [settingsRow];

    const map: Record<string, { action: string; process: string }> = {
      '1231': { action: 'qwxryb', process: 'GAIN1231_SAVE' },
      'capital': { action: 'c7ivwh', process: 'CAPITAL_GAIN_LOSS_SAVE' },
      'charitable': { action: 'zkiwtz', process: 'CHARITABLE_CONTRIBUTION_SAVE' },
      'scheduleC': { action: 't5qeqa', process: 'SAVEDRDWS' },
    };
    const saveCfg = map[this.activeView];
    if (!saveCfg) {
      this.saving = false;
      return;
    }
    const rowsToSave = this.activeView === 'scheduleC'
      ? this.composeScheduleCSaveRows()
      : this.detailRows;
    if (!Array.isArray(rowsToSave) || rowsToSave.length === 0) {
      this.saving = false;
      this.cdr.detectChanges();
      return;
    }
    this.service.saveObject(baseUrl, saveCfg.action, this.globalService, this.filterParams, settings, rowsToSave, saveCfg.process).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.saving = false;
        this.requestGridUpdate.emit('refresh');
        this.cdr.detectChanges();
      },
      error: () => {
        this.saving = false;
        this.cdr.detectChanges();
      },
    });
  }

  onRefresh(): void {
    this.loadActiveTab();
    this.requestGridUpdate.emit('refresh');
  }

  onCancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  get isSummaryScreen(): boolean {
    return this.activeView === 'summary';
  }

  get isScheduleCScreen(): boolean {
    return this.activeView === 'scheduleC';
  }

  /** Bottom Save/Reset toolbar visibility — mirrors each legacy template's modal-footer ng-show. */
  get showFooterButtons(): boolean {
    if (this.isSummaryScreen) return false;
    if (this.activeView === '1231') return true;
    if (this.activeView === 'scheduleC') return false; // scheduleC has its own inline action bar
    const gt = String(this.groupType || '').toUpperCase();
    if (this.activeView === 'capital' || this.activeView === 'charitable') {
      return gt === 'GROUP';
    }
    return false;
  }

  /** Capital Loss Analysis — rows used in the "Capital Loss Limitation" table (LINE_TYPE filter). */
  get capitalLimitationRows(): any[] {
    return (this.detailRows || []).filter((row) => row?.LINE_TYPE === 'CAPITAL_LOSS_LIMITAION');
  }

  /** Capital Loss Analysis — carryforward section iterates the full list, legacy uses $index>4. */
  get capitalCarryforwardRows(): { row: any; index: number }[] {
    return (this.detailRows || []).map((row, index) => ({ row, index })).filter((entry) => entry.index > 4);
  }

  shouldShowCapitalComment(row: any): boolean {
    return row?.LINE_DESCRIPTION === 'Form 8916 Line 2c1' || row?.LINE_DESCRIPTION === 'Form 8916 Line 2c2';
  }

  shouldShowCapitalOverrideInput(row: any): boolean {
    return row?.LINE_TYPE === 'CAPITAL_LOSS_LIMITAION' && row?.SAVE_YN === 'Y';
  }

  trackByIndex(index: number): number { return index; }

  /** Used by readonly numeric cells to display thousands-separator formatting (legacy format="number"). */
  formatAmount(value: any): string {
    if (value === null || value === undefined || value === '') return '';
    const n = Number(value);
    if (isNaN(n)) return String(value);
    return n.toLocaleString('en-US');
  }

  private processScheduleCData(rows: any[]): void {
    this.wsHeader = {};
    this.drdRecord = {};
    this.overrideAmtRecord = {};
    this.drdWorksheetRows = [];
    const sourceRows = Array.isArray(rows) ? rows.map((row) => ({ ...row })) : [];
    if (sourceRows.length === 0) {
      this.isCalc = '';
      return;
    }
    this.isCalc = sourceRows[0]?.IS_CALC || this.isCalc || 'Y';
    this.wsHeader = sourceRows.shift() || {};
    this.overrideAmtRecord = sourceRows.length > 0 ? (sourceRows.pop() || {}) : {};
    this.drdRecord = sourceRows.length > 0 ? (sourceRows.pop() || {}) : {};
    this.drdWorksheetRows = sourceRows;
  }

  private composeScheduleCSaveRows(): any[] {
    const calcFlag = this.isCalc || 'Y';
    const worksheetRows = (this.drdWorksheetRows || []).map((row: any) => ({
      ...row,
      IS_CALC: calcFlag,
    }));
    const rows: any[] = [...worksheetRows];
    if (this.drdRecord && Object.keys(this.drdRecord).length > 0) {
      rows.push({ ...this.drdRecord, IS_CALC: calcFlag });
    }
    if (this.overrideAmtRecord && Object.keys(this.overrideAmtRecord).length > 0) {
      rows.push({ ...this.overrideAmtRecord, IS_CALC: calcFlag });
    }
    return rows;
  }
}
